import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Async from 'react-select/async';
import intl from 'react-intl-universal';
import Cancel from '@material-ui/icons/Cancel';
import styles from './styles';
import ToolTip from '../ToolTip';

const AutocompleteBase = ({
  defaultValue,
  label,
  renderMenu,
  renderOption,
  renderValue,
  renderRemove,
  multi,
  onChange,
  footer,
  onFilter,
  renderIcon,
  hideIconOnSelect,
  noResultsText,
  required,
  duplicateEmployee,
  displayText,
  removeAsterisk,
  ...props
}) => {
  const selectRef = useRef();
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState();
  const [searchText, setSearchText] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (duplicateEmployee) {
      handleChange(duplicateEmployee);
      selectRef.current.blur();
    }
  }, [duplicateEmployee]);

  const handleInputChange = (input, { action }) => {
    if (action === 'input-change' || action === 'set-value') {
      setSearchText(input);
    }
  };

  const handleChange = input => {
    setValue(input);
    if (onChange) {
      onChange(input || []);
    }
  };

  const customMenuList = renderMenu ? { MenuList: renderMenu } : null;
  const customLabel = multi ? { MultiValueLabel: renderValue, MultiValueRemove: renderRemove } : { SingleValue: renderValue };
  const ClearIndicator = props => {
    const {
      children = <Cancel />,
      getStyles,
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <div {...restInnerProps} ref={ref} style={getStyles('clearIndicator', props)}>
        {children}
      </div>
    );
  };
  return (
    <React.Fragment>
      <ToolTip title={label} hideButton={true} placement="bottom-start">
        <div className="autocomplete-custom" title={value ? value.displayText : ''}>
          <div className={'labelPlaceholder' + (props.styleType === 'addEmployee' ? ' placeholderFaded' : '')} />
          <Async
            classNamePrefix="autocomplete-control"
            ref={selectRef}
            placeholder={''}
            aria-label={label}
            value={value}
            isMulti={multi}
            cacheOptions
            defaultOptions
            components={{
              ...customMenuList,
              ...customLabel,
              Option: renderOption,
              DropdownIndicator: hideIconOnSelect ? renderIcon : null,
              IndicatorSeparator: () => null,
              ClearIndicator: ClearIndicator,
            }}
            styles={styles}
            filterOption={onFilter}
            onInputChange={handleInputChange}
            onChange={handleChange}
            onBlur={() => setIsFocused(false)}
            onFocus={() => setIsFocused(true)}
            onMenuOpen={() => setIsMenuOpen(true)}
            onMenuClose={() => setIsMenuOpen(false)}
            noOptionsMessage={() => (searchText ? noResultsText || intl.get('autocomplete.noResults') : 'Type to search')}
            isClearable={!multi ? true : false}
            {...props}
          />

          <label
            onClick={() => selectRef.current.focus()}
            className={'floatLabel ' + (removeAsterisk ? 'floatLabelShrinkAsterisk' : isFocused || value ? 'floatLabelShrink' : '')}>
            {label}
            {required && <span className="asterisk">*</span>}
          </label>
        </div>
      </ToolTip>
      {footer && footer({ value, searchText, isFocused, isMenuOpen })}
    </React.Fragment>
  );
};

AutocompleteBase.defaultProps = {
  footer: null,
  onFilter: null,
  onChange: null,
  renderOption: null,
  renderValue: null,
  renderRemove: null,
  renderIcon: null,
};

AutocompleteBase.propTypes = {
  /** Default value to seed uncontrolled component */
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Floating label */
  label: PropTypes.string.isRequired,
  /** Function to override default option rendering */
  renderOption: PropTypes.func,
  /** Function to override default value rendering */
  renderValue: PropTypes.func,
  /** Function to override default icon rendering */
  renderRemove: PropTypes.func,
  /** Boolean to determine single/multiple value rendering */
  multi: PropTypes.bool.isRequired,
  /** Add custom footer after AutoComplete */
  footer: PropTypes.func,
  /** Function to override default filtering */
  onFilter: PropTypes.func,
  /** Callback function that fires when autocomplete adds/removes values */
  onChange: PropTypes.func.isRequired,
  /** Function to override default arrowRenderer */
  renderIcon: PropTypes.func,
  /** Boolean to determine search/remove icon rendering */
  hideIconOnSelect: PropTypes.bool,
};

export default AutocompleteBase;
